// The check-in deck — the questions Oreia has, one at a time, with an end.
//
// The stack this replaces had no count and no finish: answering one revealed the next, forever,
// which reads as debt rather than reflection. A deck is bounded by construction. You can see how
// many there are, you can see how far you've come, and when you reach the end something says so
// instead of the section simply vanishing.
//
// The set is already paced by the server (check-in-pacing.ts), so "3 of 3" here is genuinely all
// that is being asked today, not the first page of twenty.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDEK } from '../auth/SessionProvider';
import { fetchCheckIns, respondToCheckIn, type PatternCheckIn } from '../api/check-ins';
import { CheckInCard } from './CheckInCard';
import { IconArrow, IconLock, IconSpark } from '../components/Icons';
import { FullPageSpinner } from '../components/ui';

export function CheckInDeckScreen() {
  const navigate = useNavigate();
  const dek = useDEK();

  const [queue, setQueue] = useState<PatternCheckIn[] | null>(null);
  const [totalOpen, setTotalOpen] = useState(0);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const headingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ac = new AbortController();
    fetchCheckIns(dek, ac.signal)
      .then((q) => { setQueue(q.checkIns); setTotalOpen(q.totalOpen); })
      .catch(() => setQueue([]));
    return () => ac.abort();
  }, [dek]);

  const current = queue && index < queue.length ? queue[index] : null;
  const done = queue !== null && !current;

  // Move focus to each new question so the deck is answerable without a mouse, and so a screen
  // reader lands on the thing that changed rather than at the top of the page.
  useEffect(() => {
    if (current || done) headingRef.current?.focus();
  }, [index, current, done]);

  function advance(response: string | null) {
    if (response) {
      const id = current?.id;
      setAnswers((a) => [...a, response]);
      if (id) void respondToCheckIn(id, response).catch(() => {});
    }
    setIndex((i) => i + 1);
  }

  const summary = useMemo(() => summarise(answers), [answers]);
  const total = queue?.length ?? 0;
  const remaining = Math.max(0, totalOpen - total);

  if (queue === null) return <FullPageSpinner label="Opening your check-ins" />;

  return (
    <div className="odeck">
      <div className="odeck-top">
        <button type="button" className="odeck-back" onClick={() => navigate('/self')} aria-label="Back to Self">
          <IconArrow style={{ width: 18, height: 18, transform: 'rotate(180deg)' }} />
        </button>
        <div className="olock"><IconLock /> Private</div>
      </div>

      <div
        className="odeck-head"
        ref={headingRef}
        tabIndex={-1}
        aria-live="polite"
      >
        <h1 className="odeck-title">Oreia is checking in</h1>
        {total > 0 ? (
          <span className="omono">{done ? `${total} of ${total}` : `${index + 1} of ${total}`}</span>
        ) : null}
      </div>

      {total > 0 ? (
        <div className="odeck-progress" role="presentation">
          <div className="odeck-progress-fill" style={{ width: `${(Math.min(index, total) / total) * 100}%` }} />
        </div>
      ) : null}

      {current ? (
        <div className="odeck-stage">
          {/* Two faint edges behind the card: the volume is depicted rather than scrolled. */}
          {total - index > 1 ? <div className="odeck-peek odeck-peek-2" aria-hidden /> : null}
          {total - index > 2 ? <div className="odeck-peek odeck-peek-3" aria-hidden /> : null}
          <div className="odeck-card" key={current.id}>
            <CheckInCard
              checkIn={current}
              onAnswer={(response) => advance(response)}
              onDefer={() => advance(null)}
            />
          </div>
        </div>
      ) : (
        <div className="odeck-done">
          <IconSpark style={{ width: 22, height: 22, opacity: 0.7 }} />
          <h2 className="odeck-done-title">
            {total === 0 ? 'Nothing to ask today.' : "That's everything."}
          </h2>
          <p className="odeck-done-body">
            {total === 0
              ? 'Oreia only asks when something in your writing actually changes. Nothing has, lately.'
              : summary}
          </p>
          {remaining > 0 ? (
            <p className="omono odeck-done-note">
              {remaining} more {remaining === 1 ? 'is' : 'are'} waiting — they&apos;ll come another day.
            </p>
          ) : null}
          <button type="button" className="octa odeck-done-cta" onClick={() => navigate('/self')}>
            Back to Self
          </button>
        </div>
      )}
    </div>
  );
}

/** What the person just told Oreia, said back to them so the answers visibly landed somewhere. */
function summarise(answers: string[]): string {
  if (answers.length === 0) return 'You skipped these for now. They will keep.';

  const counts = new Map<string, number>();
  for (const a of answers) counts.set(a, (counts.get(a) ?? 0) + 1);

  const phrases: string[] = [];
  const say = (id: string, one: string, many: string) => {
    const n = counts.get(id);
    if (n) phrases.push(`${n} ${n === 1 ? one : many}`);
  };
  say('still_present', 'still present', 'still present');
  say('improving', 'easing', 'easing');
  say('resolved', 'resolved', 'resolved');
  say('comes_and_goes', 'that comes and goes', 'that come and go');
  say('context_specific', 'situational', 'situational');
  say('never_fit', 'that never fit', 'that never fit');

  const list = phrases.length > 1
    ? `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`
    : phrases[0];
  return `You marked ${list}. Oreia will weight what you said.`;
}
